/**
 * Payroll Period detail — lifecycle actions + payslip roster + report downloads.
 *
 * DRAFT → IMPORTED → CALCULATED → PENDING_APPROVAL → APPROVED → PAID
 *                          ↑              │
 *                          └── REJECTED ──┘ (reopen() sends it back to CALCULATED)
 *
 * Endpoints: see payrollApi (periods.*, payslipsForPeriod, reports.*)
 */
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, Calculator, CheckCircle2, FileSpreadsheet, Landmark,
  Pencil, RotateCcw, Send, Trash2, Wallet, XCircle,
} from "lucide-react";
import { useAuth } from "#/context/AuthProvider";
import { ReasonModal } from "#/components/common/ReasonModal";
import { payrollApi, downloadBlob } from "../api";
import { useAsyncData, useMutation, usePagination, useVoidMutation, getErrorMessage } from "../hooks";
import { monthLabel } from "../types";
import type { EmployeeBankAccountRequest, MissingBankAccount, SalaryRecord } from "../types";
import { PayrollApprovalTimeline } from "../components/PayrollApprovalTimeline";
import {
  BackBtn, Btn, Card, CardHeader, EmptyState, LinkBtn, PageHeader,
  Pagination, PayrollError, PayrollLoading, PeriodStatusBadge,
  Table, TBody, TD, TH, THead, TR, fmt,
} from "../components/PayrollUI";

const inp = "w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-right text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";
const bankInp = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";

// Editing/deleting an imported salary record is only allowed before the period is submitted
// for approval — "you can revert or inline edit before it's submitted, after that you cannot
// edit" — mirrors PayrollImportService.requireEditablePeriod() on the backend exactly.
const RECORD_EDITABLE_STATUSES = ["DRAFT", "IMPORTED", "CALCULATED", "REJECTED"];

type SalaryDraft = { basicPay: string; houseAllowance: string; transportAllowance: string; otherAllowance: string };

const emptyBankForm: EmployeeBankAccountRequest = { bankName: "", branchName: "", accountName: "", accountNumber: "" };

export default function PayrollPeriodDetailPage() {
  const { id } = useParams();
  const periodId = Number(id);
  const navigate = useNavigate();
  const { hasRole, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasRole("ADMIN");
  const canProcess = isAdmin || hasPermission("HR_PAYROLL_PROCESS") || hasPermission("HR_PAYROLL_MANAGE");
  const canManage = isAdmin || hasPermission("HR_PAYROLL_MANAGE");
  const canApprove = isAdmin || hasPermission("HR_PAYROLL_APPROVE");

  const [showReject, setShowReject] = useState(false);
  const [reportBusy, setReportBusy] = useState<string | null>(null);

  const { data: period, loading, error, reload } = useAsyncData(() => payrollApi.getPeriod(periodId), [periodId]);
  // Payslips are computed and persisted as soon as a period is calculated (well before
  // submit/approve), so the Officer can review them before submitting and the Approver
  // can review them before approving/rejecting — not just after the period is finalized.
  const payslipsReady = !!period && !["DRAFT", "IMPORTED"].includes(period.status);
  const payslips = useAsyncData(
    () => (payslipsReady ? payrollApi.payslipsForPeriod(periodId) : Promise.resolve([])),
    [periodId, period?.status],
  );

  // Payslips come back as a flat array (one call per period), so pagination is done
  // client-side rather than round-tripping the server for what's usually a few hundred rows.
  const rosterPagination = usePagination(0, 10);
  const rosterAll = payslips.data ?? [];
  const rosterPage = useMemo(() => {
    const size = rosterPagination.size;
    const totalPages = Math.max(1, Math.ceil(rosterAll.length / size));
    const page = Math.min(rosterPagination.page, totalPages - 1);
    return {
      content: rosterAll.slice(page * size, page * size + size),
      page,
      size,
      totalElements: rosterAll.length,
      totalPages,
    };
  }, [rosterAll, rosterPagination.page, rosterPagination.size]);

  const calculate = useVoidMutation(() => payrollApi.calculatePeriod(periodId));
  const submit = useVoidMutation(() => payrollApi.submitPeriod(periodId));
  const approve = useVoidMutation(() => payrollApi.approvePeriod(periodId));
  const reject = useMutation((reason: string) => payrollApi.rejectPeriod(periodId, reason));
  const reopen = useVoidMutation(() => payrollApi.reopenPeriod(periodId));
  const markPaid = useVoidMutation(() => payrollApi.markPaid(periodId));

  async function run(mutation: { mutate: () => Promise<unknown> }) {
    await mutation.mutate();
    reload();
  }

  async function doReject(reason: string) {
    await reject.mutate(reason);
    setShowReject(false);
    reload();
  }

  // ── Calculate failure -> inline "fix missing bank accounts" panel ──────────
  // Rather than leaving HR staring at a static error string, a failed Calculate immediately
  // looks up exactly who's missing bank details so they can be fixed right here and Calculate
  // retried, with no navigation away from the period.
  const [missingAccounts, setMissingAccounts] = useState<MissingBankAccount[] | null>(null);
  const [fixingEmployeeId, setFixingEmployeeId] = useState<number | null>(null);
  const [bankForm, setBankForm] = useState<EmployeeBankAccountRequest>(emptyBankForm);
  const [bankSaving, setBankSaving] = useState(false);
  const [bankError, setBankError] = useState<string | null>(null);

  async function doCalculate() {
    setMissingAccounts(null);
    try {
      await calculate.mutate();
      reload();
    } catch {
      try {
        const missing = await payrollApi.getMissingBankAccounts(periodId);
        setMissingAccounts(missing);
      } catch {
        // Fall back to the plain calculate.error text below — nothing more we can show.
      }
    }
  }

  function startFixBank(employeeId: number) {
    setFixingEmployeeId(employeeId);
    setBankForm(emptyBankForm);
    setBankError(null);
  }

  async function saveBankFix(employeeId: number) {
    setBankSaving(true);
    setBankError(null);
    try {
      await payrollApi.updateBankAccount(employeeId, bankForm);
      setFixingEmployeeId(null);
      setMissingAccounts((cur) => (cur ? cur.filter((m) => m.employeeId !== employeeId) : cur));
    } catch (err) {
      setBankError(getErrorMessage(err));
    } finally {
      setBankSaving(false);
    }
  }

  // ── Imported salary records — inline edit / revert, pre-submission only ────
  const recordsEditable = !!period && RECORD_EDITABLE_STATUSES.includes(period.status);
  const salaryRecords = useAsyncData(
    () => (recordsEditable ? payrollApi.listSalaryRecords(periodId) : Promise.resolve([])),
    [periodId, period?.status],
  );

  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<SalaryDraft | null>(null);
  const [rowSaving, setRowSaving] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [rowDeleting, setRowDeleting] = useState(false);

  function startEditRecord(r: SalaryRecord) {
    setEditingId(r.employeeId);
    setConfirmDeleteId(null);
    setRowError(null);
    setDraft({
      basicPay: String(r.basicPay ?? 0),
      houseAllowance: String(r.houseAllowance ?? 0),
      transportAllowance: String(r.transportAllowance ?? 0),
      otherAllowance: String(r.otherAllowance ?? 0),
    });
  }

  function cancelEditRecord() {
    setEditingId(null);
    setDraft(null);
    setRowError(null);
  }

  async function saveRecordEdit(employeeId: number) {
    if (!draft) return;
    const basicPay = Number(draft.basicPay);
    if (Number.isNaN(basicPay) || basicPay < 0) {
      setRowError("Basic pay must be a number, zero or greater");
      return;
    }
    setRowSaving(true);
    setRowError(null);
    try {
      await payrollApi.updateSalaryRecord(periodId, employeeId, {
        basicPay,
        houseAllowance: Number(draft.houseAllowance) || 0,
        transportAllowance: Number(draft.transportAllowance) || 0,
        otherAllowance: Number(draft.otherAllowance) || 0,
      });
      setEditingId(null);
      setDraft(null);
      salaryRecords.reload();
    } catch (err) {
      setRowError(getErrorMessage(err));
    } finally {
      setRowSaving(false);
    }
  }

  async function confirmRecordDelete(employeeId: number) {
    setRowDeleting(true);
    setRowError(null);
    try {
      await payrollApi.deleteSalaryRecord(periodId, employeeId);
      setConfirmDeleteId(null);
      salaryRecords.reload();
    } catch (err) {
      setRowError(getErrorMessage(err));
    } finally {
      setRowDeleting(false);
    }
  }

  async function downloadReport(kind: "summary" | "detail", format: "xlsx" | "pdf") {
    setReportBusy(`${kind}-${format}`);
    try {
      const blob = kind === "summary"
        ? await payrollApi.periodSummaryReport(periodId, format)
        : await payrollApi.periodDetailReport(periodId, format);
      downloadBlob(blob, `payroll-${kind}-${period?.periodCode ?? periodId}.${format}`);
    } finally {
      setReportBusy(null);
    }
  }

  if (loading) return <PayrollLoading label="Loading finance period…" />;
  if (error) return <PayrollError message={error} onRetry={reload} />;
  if (!period) return <PayrollError message="Finance period not found." />;

  const isBusy = calculate.loading || submit.loading || approve.loading || reject.loading || reopen.loading || markPaid.loading;

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Finance · Periods"
        title={`${monthLabel(period.periodMonth)} ${period.periodYear}`}
        description={period.periodCode}
        actions={
          <div className="flex flex-wrap gap-2">
            <BackBtn to="/payroll/periods" label="Periods" />
            <PeriodStatusBadge value={period.status} />
          </div>
        }
      />

      <PayrollApprovalTimeline period={period} />

      {/* Payslip roster — visible as soon as the period has been calculated, so both the
          Officer (before Submit) and the Approver (before Approve/Reject) can review actual
          employee earnings and net pay, not just after the period is finalized. Shown before
          the lifecycle actions below so the imported/computed data is reviewed first. */}
      <Card noPad>
        <CardHeader title="Payslips" />
        {!payslipsReady ? (
          <div className="p-5 text-sm text-slate-500">Payslips become visible once the period has been calculated.</div>
        ) : payslips.loading ? (
          <PayrollLoading />
        ) : (
          <>
            <Table>
              <THead>
                <TH>Employee</TH><TH>Department</TH><TH right>Gross Pay</TH>
                <TH right>Deductions</TH><TH right>Net Pay</TH><TH right>Actions</TH>
              </THead>
              <TBody>
                {!(rosterPage.content.length) ? (
                  <tr><td colSpan={6}><EmptyState icon={Wallet} title="No payslips" description="No payslips have been generated for this period." /></td></tr>
                ) : rosterPage.content.map((p) => (
                  <TR key={p.id}>
                    <TD>
                      <div className="font-semibold text-slate-800">{p.employeeName}</div>
                      <div className="font-mono text-xs text-slate-400">{p.employeeNumber}</div>
                    </TD>
                    <TD muted>{p.departmentName ?? "—"}</TD>
                    <TD right>{fmt(p.grossPay)}</TD>
                    <TD right muted>{fmt(p.totalDeductions)}</TD>
                    <TD right><span className="font-semibold text-emerald-700">{fmt(p.netPay)}</span></TD>
                    <TD right>
                      <Btn size="sm" onClick={() => navigate(`/payroll/payslips/${p.id}`)}>View</Btn>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            {rosterAll.length > 0 && (
              <div className="p-3">
                <Pagination page={rosterPage} onPageChange={rosterPagination.setPage} onSizeChange={rosterPagination.setSize} />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Imported salary records — the raw input Calculate reads, editable in place until the
          period is submitted for approval. "Revert" removes a bad import row for one employee;
          after PENDING_APPROVAL this whole section is hidden and the data is locked. */}
      {recordsEditable && (
        <Card noPad>
          <CardHeader
            title="Imported Salary Records"
            subtitle="Fix or remove imported figures before submitting for approval — locked once submitted"
          />
          {salaryRecords.loading ? (
            <PayrollLoading />
          ) : salaryRecords.error ? (
            <div className="p-5"><PayrollError message={salaryRecords.error} onRetry={salaryRecords.reload} /></div>
          ) : (
            <Table>
              <THead>
                <TH>Employee</TH><TH right>Basic Pay</TH><TH right>House</TH>
                <TH right>Transport</TH><TH right>Other</TH>
                {canProcess && <TH right>Actions</TH>}
              </THead>
              <TBody>
                {!(salaryRecords.data?.length) ? (
                  <tr><td colSpan={6}><EmptyState icon={FileSpreadsheet} title="No salary records" description="Import basic pay to get started." /></td></tr>
                ) : salaryRecords.data.map((r) => {
                  const editing = editingId === r.employeeId;
                  const confirming = confirmDeleteId === r.employeeId;
                  return (
                    <TR key={r.employeeId}>
                      <TD>
                        <div className="font-semibold text-slate-800">{r.employeeName ?? "—"}</div>
                        <div className="font-mono text-xs text-slate-400">{r.employeeNumber}</div>
                      </TD>
                      <TD right>
                        {editing ? (
                          <input type="number" min={0} step="0.01" className={inp} value={draft?.basicPay ?? ""}
                            onChange={(e) => setDraft((d) => (d ? { ...d, basicPay: e.target.value } : d))} />
                        ) : fmt(r.basicPay)}
                      </TD>
                      <TD right>
                        {editing ? (
                          <input type="number" min={0} step="0.01" className={inp} value={draft?.houseAllowance ?? ""}
                            onChange={(e) => setDraft((d) => (d ? { ...d, houseAllowance: e.target.value } : d))} />
                        ) : fmt(r.houseAllowance)}
                      </TD>
                      <TD right>
                        {editing ? (
                          <input type="number" min={0} step="0.01" className={inp} value={draft?.transportAllowance ?? ""}
                            onChange={(e) => setDraft((d) => (d ? { ...d, transportAllowance: e.target.value } : d))} />
                        ) : fmt(r.transportAllowance)}
                      </TD>
                      <TD right>
                        {editing ? (
                          <input type="number" min={0} step="0.01" className={inp} value={draft?.otherAllowance ?? ""}
                            onChange={(e) => setDraft((d) => (d ? { ...d, otherAllowance: e.target.value } : d))} />
                        ) : fmt(r.otherAllowance)}
                      </TD>
                      {canProcess && (
                        <TD right>
                          {editing ? (
                            <div className="flex justify-end gap-1.5">
                              <Btn size="sm" variant="ghost" onClick={cancelEditRecord}>Cancel</Btn>
                              <Btn size="sm" variant="primary" loading={rowSaving} loadingText="Saving…" onClick={() => saveRecordEdit(r.employeeId)}>Save</Btn>
                            </div>
                          ) : confirming ? (
                            <div className="flex justify-end gap-1.5">
                              <Btn size="sm" variant="ghost" onClick={() => setConfirmDeleteId(null)}>Cancel</Btn>
                              <Btn size="sm" variant="danger" loading={rowDeleting} loadingText="Removing…" onClick={() => confirmRecordDelete(r.employeeId)}>Confirm Revert</Btn>
                            </div>
                          ) : (
                            <div className="flex justify-end gap-1.5">
                              <Btn size="sm" variant="secondary" icon={Pencil} onClick={() => startEditRecord(r)}>Edit</Btn>
                              <Btn size="sm" variant="dangerOutline" icon={Trash2} onClick={() => { setConfirmDeleteId(r.employeeId); setEditingId(null); }}>Revert</Btn>
                            </div>
                          )}
                        </TD>
                      )}
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
          {rowError && <p className="px-5 pb-4 text-sm text-red-600">{rowError}</p>}
        </Card>
      )}

      {/* Lifecycle actions — placed below the data so Calculate/Submit are reviewed in
          context of what was imported/computed above, not blind before seeing the roster. */}
      <Card>
        <div className="flex flex-wrap items-center gap-2">
          {canProcess && (period.status === "DRAFT" || period.status === "IMPORTED") && (
            <LinkBtn to={`/payroll/periods/${periodId}/import`} variant="secondary" icon={FileSpreadsheet}>
              Import Basic Pay
            </LinkBtn>
          )}
          {canProcess && ["IMPORTED", "CALCULATED", "REJECTED"].includes(period.status) && (
            <Btn variant="secondary" icon={Calculator} loading={calculate.loading} loadingText="Calculating…" onClick={doCalculate}>
              {period.status === "IMPORTED" ? "Calculate" : "Recalculate"}
            </Btn>
          )}
          {canProcess && period.status === "CALCULATED" && (
            <Btn variant="primary" icon={Send} loading={submit.loading} loadingText="Submitting…" onClick={() => run(submit)}>
              Submit for Approval
            </Btn>
          )}
          {canApprove && period.status === "PENDING_APPROVAL" && (
            <>
              <Btn variant="emerald" icon={CheckCircle2} loading={approve.loading} loadingText="Approving…" onClick={() => run(approve)}>
                Approve
              </Btn>
              <Btn variant="dangerOutline" icon={XCircle} onClick={() => setShowReject(true)}>
                Reject
              </Btn>
            </>
          )}
          {canManage && period.status === "REJECTED" && (
            <Btn variant="secondary" icon={RotateCcw} loading={reopen.loading} loadingText="Reopening…" onClick={() => run(reopen)}>
              Reopen
            </Btn>
          )}
          {canProcess && period.status === "APPROVED" && (
            <Btn variant="primary" icon={Wallet} loading={markPaid.loading} loadingText="Marking Paid…" onClick={() => run(markPaid)}>
              Mark Paid
            </Btn>
          )}
          {canProcess && ["IMPORTED", "CALCULATED", "PENDING_APPROVAL", "APPROVED", "REJECTED", "PAID"].includes(period.status) && (
            <LinkBtn to={`/payroll/periods/${periodId}/import`} variant="ghost">
              View Import Batches
            </LinkBtn>
          )}
        </div>
        {(calculate.error || submit.error || approve.error || reject.error || reopen.error || markPaid.error) && (
          <p className="mt-3 text-sm text-red-600">
            {calculate.error || submit.error || approve.error || reject.error || reopen.error || markPaid.error}
          </p>
        )}
      </Card>

      {/* Inline fix-it panel — appears only when Calculate fails because one or more employees
          are missing bank details, so HR can add them right here and retry without leaving
          the page. */}
      {missingAccounts && missingAccounts.length > 0 && (
        <Card className="border-l-4 border-l-amber-400">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800">Missing bank account details</p>
              <p className="mt-0.5 text-sm text-amber-700">
                {missingAccounts.length} employee{missingAccounts.length !== 1 ? "s" : ""} can&apos;t be paid until bank details are on file. Add them here, then Calculate again.
              </p>
              <div className="mt-3 space-y-2">
                {missingAccounts.map((m) => (
                  <div key={m.employeeId} className="rounded-xl border border-amber-200 bg-white p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <span className="font-semibold text-slate-800">{m.employeeName || "—"}</span>
                        <span className="ml-2 font-mono text-xs text-slate-400">{m.employeeNumber}</span>
                      </div>
                      {fixingEmployeeId !== m.employeeId && (
                        <Btn size="sm" variant="secondary" icon={Landmark} onClick={() => startFixBank(m.employeeId)}>
                          Add Bank Details
                        </Btn>
                      )}
                    </div>
                    {fixingEmployeeId === m.employeeId && (
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        <input className={bankInp} placeholder="Bank Name" value={bankForm.bankName ?? ""}
                          onChange={(e) => setBankForm((f) => ({ ...f, bankName: e.target.value }))} />
                        <input className={bankInp} placeholder="Branch" value={bankForm.branchName ?? ""}
                          onChange={(e) => setBankForm((f) => ({ ...f, branchName: e.target.value }))} />
                        <input className={bankInp} placeholder="Account Name" value={bankForm.accountName ?? ""}
                          onChange={(e) => setBankForm((f) => ({ ...f, accountName: e.target.value }))} />
                        <input className={bankInp} placeholder="Account Number" value={bankForm.accountNumber ?? ""}
                          onChange={(e) => setBankForm((f) => ({ ...f, accountNumber: e.target.value }))} />
                        {bankError && <p className="text-xs text-red-600 sm:col-span-2">{bankError}</p>}
                        <div className="flex justify-end gap-2 sm:col-span-2">
                          <Btn size="sm" variant="ghost" onClick={() => setFixingEmployeeId(null)}>Cancel</Btn>
                          <Btn size="sm" variant="primary" loading={bankSaving} loadingText="Saving…" onClick={() => saveBankFix(m.employeeId)}>Save</Btn>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <Btn variant="secondary" icon={Calculator} loading={calculate.loading} loadingText="Calculating…" onClick={doCalculate}>
                  Retry Calculate
                </Btn>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Reports */}
      {(period.status === "APPROVED" || period.status === "PAID") && (
        <Card noPad>
          <CardHeader title="Reports" />
          <div className="flex flex-wrap gap-2 p-5">
            <Btn variant="secondary" loading={reportBusy === "summary-xlsx"} loadingText="Preparing…" onClick={() => downloadReport("summary", "xlsx")}>Summary (Excel)</Btn>
            <Btn variant="secondary" loading={reportBusy === "summary-pdf"} loadingText="Preparing…" onClick={() => downloadReport("summary", "pdf")}>Summary (PDF)</Btn>
            <Btn variant="secondary" loading={reportBusy === "detail-xlsx"} loadingText="Preparing…" onClick={() => downloadReport("detail", "xlsx")}>Detail (Excel)</Btn>
            <Btn variant="secondary" loading={reportBusy === "detail-pdf"} loadingText="Preparing…" onClick={() => downloadReport("detail", "pdf")}>Detail (PDF)</Btn>
          </div>
        </Card>
      )}

      <ReasonModal
        open={showReject}
        title="Reject Finance Period"
        label="Rejection Reason"
        placeholder="Why is this period being rejected?"
        required
        confirmLabel="Confirm Rejection"
        confirmTone="danger"
        submitting={reject.loading || isBusy}
        error={reject.error}
        onCancel={() => setShowReject(false)}
        onConfirm={doReject}
      />
    </div>
  );
}
